import {
  documentKeys,
  type DocumentMeta,
  EditorErrorBoundary,
  getDocumentList,
} from "@prismicio/editor-fields"
import {
  Box,
  Card,
  CardFooter,
  CardHeader,
  Skeleton,
  Table,
  TableBody,
  Text,
} from "@prismicio/editor-ui"
import { useSuspenseQuery } from "@tanstack/react-query"
import { type ReactNode, Suspense, useState } from "react"
import { Link, useLocation, useSearchParams } from "react-router-dom"

import { staticEditorConfig } from "../../app/editorConfig"
import { preloadPreviewUrls } from "../../app/services/core"
import {
  preloadReleasePermissions,
  type Release as ReleaseType,
  usePrefetchReleaseSize,
} from "../../app/services/release"
import { CreateNewDocumentModal } from "../DocumentListBody/CreateNewDocumentDialog"
import { UpcomingDocumentListEmptyBody } from "../DocumentListBody/DocumentListBody"
import {
  DocumentListLoadingTable,
  DocumentListTableHeader,
  DocumentListTableRow,
  getColumnLayoutWithoutCheckbox,
} from "../DocumentListBody/DocumentListTable"
import { useRepositoryLanguages } from "../useDocumentListSearchParams"
import { ReleaseDate } from "./ReleaseDate"
import { ReleaseDeleteButton } from "./ReleaseDeleteButton"
import { ReleasePreview } from "./ReleasePreview"
import { ReleasePublishButton } from "./ReleasePublishButton"
import {
  ReleaseStatusIndicator,
  ReleaseStatusIndicatorSkeleton,
} from "./ReleaseStatusIndicator"

interface ReleaseProps {
  release: ReleaseType
}

export function Release(props: ReleaseProps) {
  const { release } = props

  preloadReleasePermissions(release.id)
  usePrefetchReleaseSize(release.id)
  preloadPreviewUrls()

  return (
    <EditorErrorBoundary>
      <Suspense fallback={<ReleaseSkeleton />}>
        <ReleaseContent release={release} />
      </Suspense>
    </EditorErrorBoundary>
  )
}

function ReleaseContent(props: ReleaseProps) {
  const { release } = props

  const {
    data: { results: documents, total: totalDocuments },
  } = useReleaseDocumentList(release.id)

  const { search } = useLocation()

  return (
    <Card paddingBlock={0}>
      <CardHeader>
        <Box
          gap={8}
          padding={{ block: 18 }}
          minWidth={0}
          maxWidth={400}
          alignItems="center"
        >
          <ReleaseStatusIndicator release={release} />
          <Link
            to={`/upcoming/${release.id}${search}`}
            style={{ width: "100%" }}
          >
            <Text
              color="grey12"
              variant="emphasized"
              underline
              noWrap
              sx={{ flexGrow: 1 }}
              title={release.label}
            >
              {release.label ?? "..."}
            </Text>
          </Link>
          <ReleaseDate release={release} />
        </Box>

        <Box gap={8}>
          <ReleasePreview
            releaseId={release.id}
            size="small"
            releaseDocument={documents[0]}
          />
          <ReleaseDeleteButton release={release} size="small" />
          <ReleasePublishButton release={release} size="small" color="grey" />
        </Box>
      </CardHeader>
      <CardFooter hasPadding={false}>
        <ReleaseDocumentList
          documents={documents}
          totalDocuments={totalDocuments}
          release={release}
        />
      </CardFooter>
    </Card>
  )
}

function useReleaseDocumentList(releaseId: string) {
  const { coreApiBaseUrl, authStrategy } = staticEditorConfig

  const [searchParams] = useSearchParams()
  const { masterLocaleId } = useRepositoryLanguages()

  const language = searchParams.get("language") || masterLocaleId

  return useSuspenseQuery({
    queryKey: documentKeys.list({
      baseUrl: coreApiBaseUrl,
      authStrategy,
      language,
      statuses: [`release:${releaseId}`],
      limit: 4,
    }),
    queryFn: ({ queryKey: [, , params], signal }) =>
      getDocumentList({
        signal,
        ...params,
      }),
  })
}

interface ReleaseDocumentListProps {
  documents: DocumentMeta[]
  totalDocuments: number
  release: ReleaseType
}

function ReleaseDocumentList(props: ReleaseDocumentListProps) {
  const { documents, totalDocuments, release } = props
  const [modalOpen, setModalOpen] = useState(false)

  if (totalDocuments === 0) {
    return (
      <>
        <UpcomingDocumentListEmptyBody setModalOpen={setModalOpen} />
        <CreateNewDocumentModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          releaseId={release.id}
        />
      </>
    )
  }

  return (
    <Box flexDirection="column">
      <Table
        columnLayout={getColumnLayoutWithoutCheckbox({
          pageOptions: false,
        })}
        border={false}
        radius={false}
      >
        <DocumentListTableHeader checkbox={false} />
        <TableBody>
          {documents.map((document) => (
            <DocumentListTableRow
              // TODO: Add status to key because of an API bug where the document id can be returned twice PBD-1082
              key={`${document.id}-${document.versions[0]?.status ?? ""}`}
              document={document}
              checkbox={false}
            />
          ))}
        </TableBody>
      </Table>
      <ReleaseDocumentCount
        documents={documents}
        totalDocuments={totalDocuments}
        release={release}
      />
    </Box>
  )
}

interface ReleaseDocumentCountProps {
  documents: DocumentMeta[]
  totalDocuments: number
  release: ReleaseType
}

function ReleaseDocumentCount(props: ReleaseDocumentCountProps) {
  const { documents, totalDocuments, release } = props
  const { search } = useLocation()

  return (
    <ReleaseDocumentCountLayout>
      <Text color="grey11">
        {documents.length} of {totalDocuments}{" "}
        {totalDocuments === 1 ? "document" : "documents"}
      </Text>
      {documents.length !== totalDocuments && (
        <Link to={`/upcoming/${release.id}${search}`}>
          <Text color="grey11" underline>
            View all
          </Text>
        </Link>
      )}
    </ReleaseDocumentCountLayout>
  )
}

interface ReleaseDocumentCountLayoutProps {
  children: ReactNode
}

function ReleaseDocumentCountLayout(props: ReleaseDocumentCountLayoutProps) {
  const { children } = props

  return (
    <Box
      flexGrow={1}
      justifyContent="center"
      backgroundColor="grey2"
      borderRadius={{
        bottomLeft: 6,
        bottomRight: 6,
      }}
      padding={{
        block: 12,
      }}
      border={{
        top: true,
      }}
      gap={4}
    >
      {children}
    </Box>
  )
}

export function ReleaseSkeleton() {
  return (
    <Card paddingBlock={0}>
      <CardHeader>
        <Box gap={8} padding={{ block: 18 }} minWidth={0}>
          <ReleaseStatusIndicatorSkeleton />
          <Skeleton width={100} height={28} />
          <Skeleton width={125} height={28} />
        </Box>

        <Skeleton width={100} height={28} />
      </CardHeader>
      <CardFooter hasPadding={false}>
        <Box flexDirection="column">
          <DocumentListLoadingTable
            columns={getColumnLayoutWithoutCheckbox({
              pageOptions: false,
            })}
            checkbox={false}
            border={false}
            radius={false}
            numberOfRows={3}
          />

          <ReleaseDocumentCountLayout>
            <Skeleton width={200} height={24} />
          </ReleaseDocumentCountLayout>
        </Box>
      </CardFooter>
    </Card>
  )
}
